import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Chip, Divider, Icon, StepContent } from "@mui/material";
import { WcTypography } from "../data-display/WcTypography/WcTypography";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";

export interface WcStepProps {
  label: string;
  value: string;
  optional?: boolean;
  canReturnEarly?: boolean;
}

export interface WcLabels {
  title: string;
  optional: string;
  completed: string;
  reset: string;
  back: string;
  skip: string;
  next: string;
  finish: string;
}

export interface WcStepperProps {
  steps: WcStepProps[];
  labels: WcLabels;
  maxWidth?: number | string;
  disableControls?: boolean;
  currentStep?: string;
  onStepChange?: (value: string) => void;
  handleNext?: (value: string) => boolean;
  handleBack?: (value: string) => boolean;
  handleReset?: (value: string) => boolean;
}

export const WcVerticalStepper = (props: WcStepperProps) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean;
  }>({});

  const [errors, setErrors] = React.useState<{
    [k: number]: string;
  }>({});

  const { steps, labels, disableControls, onStepChange } = props;

  const isStepOptional = (step: number) => {
    if (step < 0 || step >= steps.length) {
      return false;
    }
    return steps[step].optional || false;
  };

  const canStepReturnEarly = (step: number) => {
    if (step < 0 || step >= steps.length) {
      return false;
    }
    return steps[step].canReturnEarly || false;
  };

  const isStepSkipped = (step: number) => skipped.has(step);

  React.useEffect(() => {
    if (props.currentStep) {
      const index = steps.findIndex((step) => step.value === props.currentStep);
      // See if we are at the last step
      if (index === steps.length - 1) {
        setActiveStep(steps.length);
      } else if (index >= 0) {
        setActiveStep(index);
      }

      // Set all previous steps as completed
      for (let i = 0; i < index; i++) {
        setCompleted({
          ...completed,
          [i]: true,
        });
      }

      // Set errors for everything after this step if it's a step that returns early
      if (canStepReturnEarly(index)) {
        handleReturnEarly();
      }
    }
  }, []);

  const totalSteps = () => steps.length;

  const isLastStep = () => activeStep === totalSteps() - 1;

  const handleReturnEarly = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }
    // Set errors for everything after this step
    for (let i = activeStep; i < totalSteps(); i++) {
      setErrors({
        ...errors,
        [i]: "This step is incomplete",
      });
    }
    if (onStepChange) onStepChange(steps[activeStep].value);
    setActiveStep(totalSteps());
    setSkipped(newSkipped);
  };

  const handleNext = () => {
    if (props.handleNext && !props.handleNext(steps[activeStep].value)) {
      return;
    }
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }
    const nextStepOutOfRange = activeStep + 1 >= totalSteps();
    if (onStepChange && !nextStepOutOfRange) onStepChange(steps[activeStep + 1].value);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);

    // Check if completed, if so set all to completed
    setCompleted({
      ...completed,
      [activeStep]: true,
    });

    // Remove errors for this step
    setErrors({
      ...errors,
      [activeStep]: undefined,
    });
  };

  const handleBack = () => {
    if (props.handleBack && !props.handleBack(steps[activeStep].value)) {
      return;
    }
    if (onStepChange) onStepChange(steps[activeStep - 1].value);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setCompleted({
      ...completed,
      [activeStep]: false,
    });
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn't optional.");
    }

    const nextStepOutOfRange = activeStep + 1 >= totalSteps();
    if (onStepChange && !nextStepOutOfRange) onStepChange(steps[activeStep + 1].value);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
    setCompleted({
      ...completed,
      [activeStep]: true,
    });
  };

  const handleReset = () => {
    if (props.handleReset && !props.handleReset(steps[0].value)) {
      return;
    }
    if (onStepChange) onStepChange(steps[0].value);
    setActiveStep(0);
    setCompleted({});
  };

  return (
    <Box sx={{ maxWidth: props.maxWidth ? props.maxWidth : 400 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          justifyItems: "center",
          gap: 2,
          p: 3,
          mb: 2,
        }}
      >
        <WcTypography variant="h6" color="textPrimary">
          {labels.title}
        </WcTypography>
        {activeStep === steps.length && <Chip label={labels.completed} color="success" variant="filled" />}
      </Box>
      <Divider
        sx={{
          mb: 3,
          mt: 1,
        }}
      />
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.value} completed={completed[index]}>
            <StepLabel
              optional={isStepOptional(index) ? <Typography variant="caption">{labels.optional}</Typography> : null}
              error={errors[index] !== undefined}
            >
              {step.label}
            </StepLabel>
            {disableControls !== true && (
              <StepContent>
                <Box sx={{ mb: 2 }}>
                  <div>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      // startIcon={<Icon component={FaBackward} />}
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      {labels.back}
                    </Button>
                    {isStepOptional(activeStep) && (
                      <Button size="small" variant="contained" color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                        {labels.skip}
                      </Button>
                    )}
                    {!canStepReturnEarly(index) && (
                      <Button
                        size="small"
                        variant="contained"
                        endIcon={<Icon component={isLastStep() ? IoMdCheckmarkCircleOutline : null} />}
                        onClick={handleNext}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        {isLastStep() ? labels.completed : labels.next}
                      </Button>
                    )}
                    {!isLastStep() && canStepReturnEarly(index) && (
                      <Button
                        size="small"
                        variant="contained"
                        endIcon={<Icon component={IoMdCheckmarkCircleOutline} />}
                        onClick={handleReturnEarly}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        {labels.completed}
                      </Button>
                    )}
                  </div>
                </Box>
              </StepContent>
            )}
          </Step>
        ))}
      </Stepper>
      {activeStep === steps.length && !props.disableControls && (
        <Button onClick={handleReset} sx={{ mt: 2, mr: 1 }} size="small">
          {labels.reset}
        </Button>
      )}
    </Box>
  );
};

export default WcVerticalStepper;
